const _ = require("lodash");

// Given a mapping function, return the first output of the mapping function
// that is not null or undefined. Like Array#find but it returns the predicate
// output instead of the original array element. Returns null if no such
// element is found.
const getFirstMappedEl = function(arr, f) {
	for (let i = 0; i < arr.length; i += 1) {
		const ret = f(arr[i]);
		if (ret !== null && ret !== undefined) {
			return ret;
		}
	}
	return null;
};

// Given a 2D array arr, determine if any of its elements match the array el.
// e.g. isElInArray([[1,2],[3,4]], [3,4]) is true.
const isElInArray = function(arr, el) {
	return _.some(arr, x => _.isEqual(x, el));
};

const isElUniqInArray = function(arr, el) {
	let count = 0;
	arr.forEach(e => {
		if (_.isEqual(e, el)) {
			count++;
		}
	});
	return count < 2;
};

// Given a 2D array arr, remove the provided element if it exists.
const pullEl = function(arr, el) {
	return _.remove(arr, x => !_.isEqual(x, el));
};

// Given an array 'arr' of three element arrays [x,y,z] and a two element array
// 'el' [x,y], return z for an element of 'arr' with matching x,y.
const getContentFromArray = function(arr, el) {
	for (let i = 0; i < arr.length; i++) {
		if (arr[i][0] === el[0] && arr[i][1] === el[1]) {
			return arr[i][2];
		}
	}
	return null;
};

// Returns a matrix of given dimensions with specified fill element.
const makeMatrix = function(width, height, fill = null) {
	return _.map(Array(height), () => _.fill(Array(width), fill));
};

//Given a matrix of the form [r1, r2, r3...] where rn is a row and whether or not to rotate counterclockwise
//return a new matrix that is rotated either clockwise (direction 1) or counterclockwise (direction -1)
const rotateMatrix = function(arr, counterclockwise) {
	return arr[0].map((x, ind) =>
		counterclockwise
			? arr.map(a => a[a.length - 1 - ind])
			: _.reverse(arr.map(a => a[ind]))
	);
};

//Given a matrix of the form [r1, r2, r3...] where rn is a row, return a new matrix where each row is flipped
//If vertical is set to true, flip every column instead
const flipMatrix = function(arr, vertical) {
	return vertical
		? _.reverse(_.cloneDeep(arr))
		: arr.map(row => _.reverse(_.cloneDeep(row)));
};

// Given the number of rows in a grid, and an array [x,y] or [x,y,...] giving
// coordinates, return the coordinates after reversing the vertical axis.
// Also allows the x and y to be offset as needed and passes any extra elements
// along, since we do weird stuff with coordinate arrays with extra data tacked on.
const flipYCoordsWithOffset = function(
	coords,
	rows,
	{xOffset = 0, yOffset = 0} = {}
) {
	const newCoordList = _.clone(coords);
	const fixPoint = coordPair => {
		if (coordPair.length === 0) return [];
		let [x, y] = coordPair;
		const extraData = coordPair.slice(2);
		x += xOffset;
		y = rows - y - 1 + yOffset;
		return [x, y].concat(extraData);
	};
	if (getArrayDepth(newCoordList) === 1) {
		return fixPoint(newCoordList);
	} else {
		return newCoordList.map(fixPoint);
	}
};

// Given an array, creates a new array such that arr[i][j] = newArr[j][i]
// [[1, 2, 3], [4, 5, 6], [7, 8, 9]] => [[1, 4, 7], [2, 5, 8], [3, 6, 9]]
// This also works for arrays that aren't necessarily n by n but are
// along the diagonal, so
// [[1, 2, 3], [4, 5], [6]] => [[1, 4, 6], [2, 5], [3]]
const flipDiagonal = function(arr) {
	const newArr = [];
	for (let x = 0; x < arr.length; x++) {
		for (let y = 0; y < arr[x].length; y++) {
			if (!newArr[y]) newArr[y] = [];
			newArr[y][x] = arr[x][y];
		}
	}
	return newArr;
};

//Given a list of arrays of the same size and a list of weights (with as many weights that sum to 1)
//returns an array that is the weighted average of those arrays using those weights
const weightedAverage = function(arrs, weights) {
	return arrs[0].map((v, i) => _.sum(weights.map((w, j) => w * arrs[j][i])));
};

// Given a index coordinate of form [x, y] and rotation direction, we return a coordinate
// within the rotated matrix
// Coordinates are in standard cartesian form
const rotateCoordinate = function(coord, gridSize, counterclockwise) {
	const coordRotated = _.map(coord, (c, dir) => {
		const index = 1 - dir;
		const transpCrd = counterclockwise
			? gridSize[index] - coord[index] - 1
			: coord[index];
		return dir === 0 ? gridSize[index] - transpCrd - 1 : transpCrd;
	});
	return coordRotated;
};

const rotateListOfCoordinates = function(
	coordinates,
	gridSize,
	counterclockwise
) {
	const rotatedGridSize = _.clone(gridSize);
	rotatedGridSize.reverse();
	return {
		rotatedCoordinates: _.map(coordinates, coord =>
			rotateCoordinate(coord, gridSize, counterclockwise)
		),
		rotatedGridSize,
	};
};

// Extends a given array to the desired length with a given default value
const extendArray = function(arr, desiredLength, val) {
	if (arr.length >= desiredLength) {
		return arr;
	}
	return _.concat(arr, _.fill(Array(desiredLength - arr.length), val));
};

// Treats null and undefined as equal when comparing, since empty positions
// in trial arrays are filled with null when sent to the DB and rehydrated.
// For other values, uses standard _.isEqual comparison.
const checkArrayEquality = (trial, submittedAnswer) => {
	return _.isEqualWith(trial, submittedAnswer, (t, s) => {
		if (_.isNull(t) || _.isUndefined(t)) {
			if (
				(_.isNull(t) && _.isUndefined(s)) ||
				(_.isUndefined(t) && _.isNull(s))
			) {
				return true;
			} else return undefined;
		}
	});
};

/*
 * Recursive function that returns how many layers deep an array is.
 *
 * @param {Array} array  The array to check the depth of.
 *
 * @param {number} depth The depth the current array is at.
 */
const getArrayDepth = array => {
	return !Array.isArray(array)
		? 0
		: 1 + Math.max(0, ...array.map(getArrayDepth));
};

module.exports = {
	getFirstMappedEl,
	isElInArray,
	isElUniqInArray,
	pullEl,
	getContentFromArray,
	flipYCoordsWithOffset,
	rotateMatrix,
	flipMatrix,
	makeMatrix,
	flipDiagonal,
	weightedAverage,
	rotateCoordinate,
	rotateListOfCoordinates,
	extendArray,
	checkArrayEquality,
	getArrayDepth,
};
